'use client';

import { useTranslation } from '@/i18n/client';
import { setI18nLang } from '@/i18n/settings';
import { useStores } from '@/models';
import { autorun } from 'mobx';
import React, { memo, useEffect, useState } from 'react';

import { CircularProgress, FormControlLabel, Radio, RadioGroup, Skeleton } from '@mui/material';
import CDNImage from '@/components/cdnImage/cdnImage';

import { Country, Currency, Language } from '@/apis/address/address.interface';

import styles from './index.module.scss';

interface IProps {
  handleGoChange: () => void;
}

const LanguageDropdown = ({ handleGoChange }: IProps) => {
  const { t } = useTranslation();
  const { countryStore } = useStores();

  const [loading, setLoading] = useState<boolean>(true);

  const [languageList, setLanguageList] = useState<Language[]>([]); // 语言列表

  const [currentLanguageData, setCurrentLanguageData] = useState<Language | null>(); // 当前语言数据
  const [currentCurrencyData, setCurrentCurrencyData] = useState<Currency | null>(); // 当前货币数据
  const [currentCountryData, setCurrentCountryData] = useState<Country | null>(); // 当前国家数据

  const [flagLoading, setFlagLoading] = useState<boolean>(true);

  useEffect(() => {
    // 监听countryStore
    const disposer = autorun(() => {
      setLoading(true);

      if (countryStore.supportLanguageList && countryStore.supportLanguageList.length > 0) {
        setLanguageList(countryStore.supportLanguageList);
        setCurrentLanguageData(countryStore.currentLanguageData);
      }

      if (countryStore.supportCurrencyList && countryStore.supportCurrencyList.length > 0) {
        setCurrentCurrencyData(countryStore.currentCurrencyData);
      }

      if (countryStore.countryList && countryStore.countryList.length > 0) {
        setCurrentCountryData(countryStore.currentCountryData);
      }

      setLoading(false);
    });

    return () => {
      disposer();
    };
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const code = (event.target as HTMLInputElement).value;
    const langConfig = languageList?.find((lang) => lang.code === code);
    if (!langConfig) return;
    setCurrentLanguageData(langConfig);

    if (langConfig.code) {
      setI18nLang(langConfig.code);
      countryStore.updateCurrentLanguage(langConfig);
    }

    window.location.reload();
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress color='inherit' size={24} />
      </div>
    );
  }

  return (
    <div className={styles.languageDropdownContainer}>
      {currentLanguageData && (
        <div>
          <div className={styles.title}>{t('setting.customise.language')}</div>
          <RadioGroup
            aria-labelledby='demo-radio-buttons-group-label'
            value={currentLanguageData.code}
            name='radio-buttons-group'
            className={styles.contentTitle}
            onChange={handleChange}
          >
            {languageList?.map((config) => (
              <FormControlLabel
                value={config.code}
                control={<Radio disableRipple />}
                label={config.displayText}
                key={config.code}
              />
            ))}
          </RadioGroup>
        </div>
      )}

      {currentCurrencyData && (
        <div>
          <div className={styles.title}>{t('setting.customise.currency')}</div>
          <span className={styles.contentTitle}>{currentCurrencyData?.displayText}</span>
          <div className={styles.changeBtnBox}>
            <span className={styles.changeBtn} onClick={handleGoChange}>
              {t('setting.customise.changeCurrency')}
            </span>
          </div>
        </div>
      )}

      {currentCountryData && (
        <div>
          <div className={styles.title}>{t('setting.customise.country')}</div>
          <div className={styles.countryInfoContainer}>
            {currentCountryData?.flagImage && (
              <div className={styles.flagContainer}>
                <Skeleton
                  variant='rectangular'
                  animation='wave'
                  className={!flagLoading ? styles.hidden : styles.flagSkeleton}
                />

                <CDNImage
                  src={currentCountryData.flagImage}
                  alt={currentCountryData.name}
                  width={24}
                  height={16}
                  className={`${styles.countryFlag} ${flagLoading ? styles.hidden : ''}`}
                  onLoad={() => setFlagLoading(false)}
                  onError={() => setFlagLoading(false)}
                />
              </div>
            )}
            <span className={styles.contentTitle}>{currentCountryData?.name ?? ''}</span>
          </div>
          <div className={styles.changeBtnBox}>
            <span className={styles.changeBtn} onClick={handleGoChange}>
              {t('setting.customise.changeCountry')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(LanguageDropdown);
